import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';

export type ConnectionType = 'existing' | 'new';

interface StripeConnectChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (connectionType: ConnectionType) => void;
  isLoading?: boolean;
}

export const StripeConnectChoiceDialog: React.FC<StripeConnectChoiceDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<ConnectionType>('existing');

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        How would you like to connect?
      </DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value as ConnectionType)}
          >
            {/* Option 1: Existing Account */}
            <Box
              sx={{
                border: '1px solid',
                borderColor: selectedOption === 'existing' ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 2,
                mb: 2,
                cursor: 'pointer',
                bgcolor: selectedOption === 'existing' ? 'primary.50' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => setSelectedOption('existing')}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        I already have a Stripe account
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Connect your existing Stripe account to receive payments. You'll keep
                      full access to your Stripe dashboard.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              />
            </Box>

            {/* Option 2: New Account */}
            <Box
              sx={{
                border: '1px solid',
                borderColor: selectedOption === 'new' ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 2,
                cursor: 'pointer',
                bgcolor: selectedOption === 'new' ? 'primary.50' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => setSelectedOption('new')}
            >
              <FormControlLabel
                value="new"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddCircleOutlineIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        I'm new to Stripe
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      We'll create a new Stripe account for you. Quick setup with guided
                      onboarding — you'll be accepting payments in minutes.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              />
            </Box>
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Connecting...' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StripeConnectChoiceDialog;
